import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import KpiProgression from "./KpiProgression";
import StatCard, { EMPTY_VALUE } from "./StatCard";
import { formatCount } from "../charts/charts.utils";

import type { JSX } from "react";

import type { CustomerTransactionsKpi } from "@/src/api/StatsAPI.types";

interface CustomerKpiDefinition {
  key: keyof CustomerTransactionsKpi;
  labelKey: string;
  isInverse?: boolean;
}

const CUSTOMER_KPI_LIST: readonly CustomerKpiDefinition[] = [
  { key: "registered_users", labelKey: "registeredUsers" },
  { key: "active_users", labelKey: "activeUsers" },
  { key: "trialing_users", labelKey: "trialing_users" },
  { key: "churn", labelKey: "churn", isInverse: true },
];

function CustomerKpiGrid(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const kpiResult = useQuery(
    StatsAPI.generateQuery({
      fn: StatsAPI.getCustomerTransactionsKpi,
      params: [{ projectSlug }],
    }),
  );
  const kpi = kpiResult.data?.data[0];

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
      {CUSTOMER_KPI_LIST.map((kpiDefinition) => {
        const metric = kpi?.[kpiDefinition.key];
        return (
          <StatCard
            key={kpiDefinition.key}
            label={t(kpiDefinition.labelKey)}
            query={kpiResult}
            scope="asOfToday"
            value={metric ? formatCount(metric.current, lang) : EMPTY_VALUE}
            footer={
              metric ? (
                <KpiProgression
                  isInverse={kpiDefinition.isInverse}
                  metric={metric}
                />
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
}

export default CustomerKpiGrid;
